"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useId, useState, type FormEvent } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useAuth } from "@/hooks/useAuth"
import { useI18n } from "@/i18n"
import type { AuthMode } from "./authMode"

interface VerifyFormProps {
  email: string
  onModeChange: (mode: AuthMode, email?: string) => void
}

export function VerifyForm({ email, onModeChange }: VerifyFormProps) {
  const { t } = useI18n()
  const { confirmSignUp, resendCode, errorKey, clearError } = useAuth()
  const [code, setCode] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [resendMessage, setResendMessage] = useState<string | null>(null)
  const codeId = useId()

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    clearError()
    setSubmitting(true)
    try {
      await confirmSignUp(email, code)
      onModeChange("signIn", email)
    } catch {
      // errorKey is already set by the auth context.
    } finally {
      setSubmitting(false)
    }
  }

  async function handleResend() {
    clearError()
    setResendMessage(null)
    try {
      await resendCode(email)
      setResendMessage(t("auth.resendCodeSent"))
    } catch {
      // errorKey is already set by the auth context.
    }
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
      <p className="text-sm text-ink-2">{t("auth.verifyIntro", { email })}</p>

      <div className="flex flex-col gap-1.5">
        <label htmlFor={codeId} className="text-sm font-medium text-ink">
          {t("auth.verifyCodeLabel")}
        </label>
        <Input
          id={codeId}
          name="code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          required
          value={code}
          onChange={event => setCode(event.target.value)}
        />
      </div>

      {errorKey && (
        <p role="alert" className="text-sm text-destructive">
          {t(errorKey)}
        </p>
      )}
      {resendMessage && (
        <p role="status" className="text-sm text-green-2">
          {resendMessage}
        </p>
      )}

      <Button type="submit" disabled={submitting} className="min-h-11">
        {t("auth.verifyButton")}
      </Button>

      <div className="flex items-center justify-between text-sm">
        <button
          type="button"
          className="font-medium text-green underline underline-offset-2"
          onClick={handleResend}
        >
          {t("auth.resendCode")}
        </button>
        <button
          type="button"
          className="text-muted-foreground underline underline-offset-2"
          onClick={() => onModeChange("signIn")}
        >
          {t("auth.backToSignIn")}
        </button>
      </div>
    </form>
  )
}
